// Spec #40: reset the password using a link emailed to the user.
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { AuthLayout } from '@/app/AuthLayout';
import { ApiException } from '@/api/errors';

export function ResetPasswordScreen() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const uid = searchParams.get('uid') ?? '';
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await resetPassword({ token, new_password: password });
      setSuccess(true);
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not reset your password.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="Invalid reset link" subtitle="This reset link is missing a token.">
        <Link to="/forgot-password" className="btn btn--primary">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle={uid ? `Resetting password for user ${uid}.` : undefined}
    >
      {success ? (
        <div className="stack">
          <div className="info-banner">
            Your password is updated. You can sign in with your new credentials.
          </div>
          <button
            className="btn btn--primary"
            onClick={() => navigate('/login', { replace: true })}
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <form className="stack" onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label htmlFor="password">New password</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            <span className="hint">At least 8 characters.</span>
          </div>
          {error ? <div className="error-banner">{error}</div> : null}
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Updating…' : 'Update password'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
